import { Cart, CartItem, Checkout, SummaryItem } from "../models/cart";
import { catchError, combineLatest, finalize, map, mergeMap, Observable, of, ReplaySubject, Subject, tap, takeUntil } from "rxjs";
import { Injectable, signal, effect } from "@angular/core";
import { LoadingService } from "./loading.service";
import { Platform } from "@ionic/angular";
import { Product, ProductAttribute, ScalablePrice } from "../models/product";
import { ProductResponse, Response } from "../models/response";
import { Router } from "@angular/router";
import { SharedService } from "./shared.service";
import { Storage } from "@ionic/storage";
import { SystemService } from "./system.service";
import { Banner } from "../models/banner";
import { GrpcPromotionService } from "./grpc-promotion.service";
import { Promotion, PROMOTION_CATEGORY } from "@app/models/promotion";
import * as PromotionModel from "../proto/promotion/model";
import { AnalyticsMapperService, AnalyticsService, UI_ACTION } from "@app/core/analytics";
import { CustomerGaugeOptions } from "./customer-gauge.service";
import {
  buildSfAdicionarCarrinhoParams,
  buildSfRemoverCarrinhoParams,
  SalesforceEventsService,
  SfCartProductContext,
} from "@app/core/salesforce";
import { sfLog } from "@app/core/salesforce/salesforce-debug.logger";

@Injectable({
  providedIn: "root",
})
export class CartService {
  categories = signal<ProductAttribute[] | undefined>([]);
  flavors = signal<ProductAttribute[] | undefined>([]);
  brands = signal<ProductAttribute[] | undefined>([]);
  recommendedProducts = signal<Product[] | undefined>([]);
  recommendedPointsProducts = signal<Product[] | undefined>([]);
  products = signal<Product[] | undefined>([]);
  promotions = signal<Product[] | undefined>([]);
  banners = signal<Banner[] | undefined>([]);
  bannersPoints = signal<Banner[] | undefined>([]);
  bannersPromotions = signal<Banner[] | undefined>([]);
  private cancelPreviousProductsRequest$ = new Subject<void>();

  private _currentCategory: ProductAttribute;
  private _currentBrand: ProductAttribute;
  private _currentFlavor: ProductAttribute | null;

  private _data: Cart = {};
  private readonly suggestedCartItemKeys = new Set<string>();
  /**
   * Quantidades alteradas no carrossel do Pedido Rápido (seleção local).
   * Só entram no body do POST /recommended; SKUs ausentes usam 1 unidade no backend.
   */
  private readonly recommendedSelections = new Map<string, number>();

  promotionImage: string = "";

  currentProduct$ = new ReplaySubject<Product | undefined>(1);
  currentProduct?: Product;
  cartItemsMap: Map<string, CartItem> = new Map();
  updateCart$ = new ReplaySubject<Cart>(1);
  updateOrders$ = new Subject<void>();

  modalIsOppened = false;
  changeProduct$ = new Subject<{ id: string; action: "add" | "update" }>();

  operation: "item" | "payment" | "delivery" = "item";

  /** Configuração da pesquisa NPS (GET /nps-customer-gauge). Signal para quem consome reagir à chegada do dado. */
  optionsGauge = signal<CustomerGaugeOptions | null>(null);

  handleCatalog$ = new ReplaySubject<void>(1);

  paymentCondition: string = "";

  intervalCheckEditFinished;

  promotionsFlavors = signal<ProductAttribute[] | undefined>(undefined);

  allPromotions: Product[] = [];

  /** Contexto de catálogo para preencher `marca` nos eventos SF quando o produto não traz brand na API. */
  private sfCartProductContext(): SfCartProductContext {
    return {
      deliveryCep: this.resolveSfDeliveryCep(),
      catalogBrand: this.currentBrand?.label,
    };
  }

  /**
   * CEP do endereço da empresa para os eventos SF de carrinho. Quando não há, o campo
   * vai vazio e o log diz exatamente o que falta no cadastro — o dado só se corrige na origem.
   */
  private resolveSfDeliveryCep(): string | undefined {
    const user = this.shared.user;
    const cep = user?.company?.address?.postalCode?.trim();
    if (cep) {
      return cep;
    }

    const motivo = !user
      ? "usuário não logado"
      : !user.company
        ? "usuário sem empresa (company) no cadastro"
        : !user.company.address
          ? "empresa sem endereço (address) no cadastro"
          : "endereço da empresa sem CEP (postalCode) no cadastro";
    sfLog.warn(`cep vazio nos eventos de carrinho: ${motivo} — corrigir no cadastro de origem`, {
      userid: user?.clientId ?? "",
      cpfCnpj: user?.cpfCnpj ?? "",
      empresa: user?.company?.name ?? "",
    });
    return undefined;
  }

  /**
   * Produto com marca/categoria reidratadas da memória de catálogo. O carrinho devolve o
   * item sem essas facetas, então operações feitas nele (remover, diminuir, limpar) só
   * conseguem informar marca ao SF/GA4 a partir do que foi guardado no add.
   */
  private withCatalogFacets(product: Product): Product {
    const hasBrand = !!(product.brand?.label || product.categories?.[1]?.label);
    const hasCategory = !!(product.category?.label || product.categories?.[0]?.label);
    if (hasBrand && hasCategory) {
      return product;
    }

    const facets = this.analyticsMapper.resolveProductFacets(product);
    const attr = (label?: string): ProductAttribute | undefined => (label ? { id: "", label } : undefined);
    return {
      ...product,
      brand: hasBrand ? product.brand : attr(facets.item_brand),
      category: hasCategory ? product.category : attr(facets.item_category),
    };
  }

  isSuggestedCartItem(item: CartItem): boolean {
    return this.collectCartItemKeys(item).some((key) => this.suggestedCartItemKeys.has(key));
  }

  private isSuggestedProduct(product: Product): boolean {
    return this.collectProductKeys(product).some((key) => this.suggestedCartItemKeys.has(key));
  }

  private markSuggestedProduct(product: Product): void {
    this.collectProductKeys(product).forEach((key) => this.suggestedCartItemKeys.add(key));
  }

  private unmarkSuggestedProduct(product: Product): void {
    this.collectProductKeys(product).forEach((key) => this.suggestedCartItemKeys.delete(key));
  }

  private collectProductKeys(product?: Partial<Product> & { code?: string; reference?: string }): string[] {
    return [
      product?.id,
      product?.sku,
      product?.productId,
      product?.code,
      product?.reference,
    ].filter((key): key is string => !!key);
  }

  private collectCartItemKeys(item?: CartItem): string[] {
    return this.collectProductKeys({
      id: item?.product?.reference,
      sku: item?.product?.code,
      productId: item?.product?.reference,
      code: item?.product?.code,
      reference: item?.product?.reference,
    });
  }

  constructor(
    public storage: Storage,
    public platform: Platform,
    public shared: SharedService,
    public loadingService: LoadingService,
    public system: SystemService,
    public router: Router,
    public grpcPromotion: GrpcPromotionService,
    private analyticsService: AnalyticsService,
    private analyticsMapper: AnalyticsMapperService,
    private sfEvents: SalesforceEventsService
  ) {
    this.currentProduct$.subscribe((current) => {
      if (current) {
        this.currentProduct = current;
        this.storage.set("currentProduct", current);
      }
    });

    // Reactive effect to manage flavors based on the current category
    effect(() => {
      const category = this.currentCategory;
      if (!category) return;

      if (category.id === "promotions") {
        this.flavors.set(this.promotionsFlavors());
      } else {
        this.getFlavors(category.id, this.currentBrand?.id).subscribe((flavors) => {
          this.flavors.set(flavors);
        });
      }
    });
  }

  loadData(): Observable<(Product[] | ProductAttribute[] | undefined)[]> {
    this.system.getSettings();
    return combineLatest([
      this.shared.get<ProductResponse<Product[]>>(`product?recommended=1`),
      this.shared.get<ProductResponse<ProductAttribute[]>>(`product/category`),
      this.initCart(),
      this.shared.get<{ banners: Banner[] }>("register/banners"),
      this.shared.get<{ banners: Banner[] }>("register/banners?is_redeem=1"),
      this.shared.get<{ banners: Banner[] }>("register/banners?is_redeem=0&type=promotion"),
      this.shared.get<any>("nps-customer-gauge"),
    ]).pipe(
      map(([resProducts, resCategories, _initCart, resBanners, resBannersPoints, resBannersPromotions, resGauge]) => {
        if (resGauge && resGauge.data && resGauge.data.surveyId) {
          const options: any = resGauge.data;
          options.surveyId = options.surveyId ? (options.surveyId as string).toLowerCase() : "";
          if (options && options.params && !options.params["Email"]) {
            options.params.Email = this.shared.user?.responsible?.email ? this.shared.user?.responsible?.email : "";
          }
          this.optionsGauge.set(options);
        } else {
          this.optionsGauge.set(null);
        }

        if (resBanners && resBanners.data && resBanners.data.banners) {
          const type = this.shared.web ? "web" : "app";
          this.banners.set(resBanners.data.banners.filter((banner) => banner.flag === type));
        } else {
          this.banners.set([]);
        }

        if (resBannersPoints && resBannersPoints.data && resBannersPoints.data.banners) {
          const type = this.shared.web ? "web" : "app";
          this.bannersPoints.set(resBannersPoints.data.banners.filter((banner) => banner.flag === type));
        } else {
          this.bannersPoints.set([]);
        }

        if (resBannersPromotions && resBannersPromotions.data && resBannersPromotions.data.banners) {
          const type = this.shared.web ? "web" : "app";
          this.bannersPromotions.set(resBannersPromotions.data.banners.filter((banner) => banner.flag === type));
        } else {
          this.bannersPromotions.set([]);
        }

        // DEBUG Pedido Rápido — remova depois de validar o retorno da API
        console.log("[Pedido Rápido] GET product?recommended=1 →", resProducts);
        console.log("[Pedido Rápido] items brutos:", resProducts?.data?.items?.length ?? 0, resProducts?.data?.items);

        if (resProducts && resProducts.data && resProducts.data.items) {
          const prods = this.getProductsWithCartPaymentDescription(this.productsDTO(resProducts.data.items));
          const recommended = prods.filter((p) => !p.is_only_redeemable);
          const recommendedPoints = prods.filter((p) => p.is_redeemable && p.redeemPoints && p.redeemPoints > 0);
          console.log("[Pedido Rápido] após filtro loja:", recommended.length, recommended);
          console.log("[Pedido Rápido] após filtro pontos:", recommendedPoints.length, recommendedPoints);
          this.recommendedProducts.set(recommended);
          this.recommendedPointsProducts.set(recommendedPoints);
          this.syncRecommendedProductsDisplay();
        } else {
          console.warn("[Pedido Rápido] sem items no retorno — limpando listas");
          this.recommendedProducts.set([]);
          this.recommendedPointsProducts.set([]);
        }
        let localCategories;
        if (resCategories && resCategories.data && resCategories.data.items) {
          localCategories = resCategories.data.items.map((cat) => {
            if (cat.images && cat.images[0] && cat.images[0].url) {
              cat.image = cat.images[0].url;
            } else {
              cat.image = "";
            }
            return cat;
          });
        } else {
          localCategories = [];
        }

        const existingCategories = this.categories() || [];
        const hasPromotions = existingCategories.find((c) => c.id === "promotions");
        if (hasPromotions && !localCategories.find((c) => c.id === "promotions")) {
          localCategories.unshift(hasPromotions);
        }
        this.categories.set(localCategories);
        this.updatePromotions();
        if (resCategories.httpStatus === 401 || resProducts.httpStatus === 401) {
          this.system.showErrorAlert(new Error("Autenticação inválida. Por favor, realize login novamente. Code: INVALID_AUTH_NO_PRODUCTS"));
          this.shared
            .logout("INVALID_AUTH_NO_PRODUCTS", {
              httpStatus: resCategories.httpStatus === 401 ? resCategories.httpStatus : resProducts.httpStatus,
              source: "HTTP",
              errorMessage: "401 ao carregar categorias ou produtos",
            })
            .subscribe();
        }
        if (resCategories.httpStatus !== 200 && resCategories.httpStatus !== 401) {
          this.system.showErrorAlert(resCategories);
        }
        return [this.recommendedProducts(), this.categories()];
      })
    );
  }

  async updatePromotions() {
    if (!this.system.enablePromotions) {
      return;
    }

    try {
      const promotionResponse = await this.grpcPromotion.getPromotions();
      const promoRes = promotionResponse.toObject();

      if (promoRes && promoRes.promotions?.length > 0) {
        if (promoRes.banner) {
          this.promotionImage = promoRes.banner.url;
        }
        const localCategories = this.categories() || [];
        if (this.promotionImage) {
          PROMOTION_CATEGORY.image = this.promotionImage;
        }
        const hasPromotion = localCategories.find((c) => c.id === "promotions");
        if (!hasPromotion) {
          localCategories.unshift(PROMOTION_CATEGORY);
          this.categories.set(localCategories);
        }
        const productsPromotions = this.promotionsToProducts(promoRes.promotions as Promotion[]);
        this.promotions.set(productsPromotions);
        this.allPromotions = productsPromotions;
      }

      const flavorResponse = await this.grpcPromotion.getPromotionFlavors();
      const flavorRes = flavorResponse.toObject();
      if (flavorRes && flavorRes.flavors) {
        this.promotionsFlavors.set(this.promotionsFlavorToDefaultFlavors(flavorRes.flavors as { name: string; id: string }[]));
      } else {
        this.promotionsFlavors.set([]);
      }

      this.handleCartPromotions();
    } catch (error) {
      console.error("[CartService] Error during updatePromotions: ", error);
      this.promotions.set([]);
      this.promotionsFlavors.set([]);
    }
  }

  promotionItemsDto(promotion: Product, itemsCart): Product {
    promotion.items = promotion.items.map((item) => {
      const promotionItemInCart = itemsCart?.find((itemCart) => {
        return itemCart.promotionId === promotion.sku && itemCart.product.code === item.sku;
      });

      if (promotionItemInCart) {
        item.quantityPromotionDetails = promotionItemInCart.quantityPromotionDetails;
      } else {
        item.quantityPromotionDetails = 0;
      }
      return item;
    });
    return promotion;
  }

  handleCartPromotions(data?) {
    if (!data) {
      data = this.data;
    }
    const promotionsCart = data.promotions || [];
    const itemsCart = data.cartItems || [];
    if (promotionsCart && promotionsCart.length > 0) {
      const promotionsWithQuantity = this.promotions()?.map((promotion) => {
        const promotionInCart: { sku: string; qnt: number; price: number } | undefined = promotionsCart?.find((p) => p.sku === promotion.sku);
        if (promotionInCart) {
          promotion.quantity = promotionInCart.qnt;
          promotion.inputQuantity = promotionInCart.qnt;
          promotion.price = promotionInCart.price;
          return this.promotionItemsDto(promotion, itemsCart);
        } else {
          promotion.quantity = 0;
          promotion.inputQuantity = 0;
          return promotion;
        }
      });

      data.cartPromotions = promotionsCart?.map((promotionCart) => this.promotions()?.find((p) => p.sku === promotionCart.sku));
      this.promotions.set(promotionsWithQuantity);
    }
    this.updateCart$.next(data);
    this.handleCatalog$.next();
  }

  promotionsFlavorToDefaultFlavors(promotionsFlavor: { name: string; id: string }[]): ProductAttribute[] {
    return promotionsFlavor.map((flavor) => {
      return {
        id: flavor.id,
        label: flavor.name,
        categoryId: "",
        image: "",
      };
    });
  }

  initIntervalCheckEditFinished() {
    if (this.data.editableUntil && this.data.isEditing) {
      clearInterval(this.intervalCheckEditFinished);

      this.intervalCheckEditFinished = setInterval(() => {
        const finishedDate = this.data.editableUntil ? new Date(this.data.editableUntil) : null;
        const now = new Date();
        if (finishedDate && finishedDate < now) {
          clearInterval(this.intervalCheckEditFinished);
          this.system.showAlert("warn", "Atenção", "O prazo para edição de pedido finalizou. Seu pedido inicial seguirá para processamento", "Ok, entendi", () => {
            const orderId = this.data.orderToken;
            this.initCart().subscribe(() => {});
            this.shared.navCtrl.navigateForward([
              `tabs/orders/${orderId}`,
              {
                back: "tabs/home",
              },
            ]);
          }, undefined, undefined, undefined, undefined, undefined, undefined, UI_ACTION.ORDER_EDIT_TIMEOUT_ACK);
        }
      }, 60000); //Check de 1min em 1min
    } else {
      if (this.intervalCheckEditFinished) {
        clearInterval(this.intervalCheckEditFinished);
      }
    }
  }

  public initCart() {
    return this.shared
      .get<Cart>(`checkout/carts`)
      .pipe(
        mergeMap((res) => {
          if (res.status) {
            return of(res);
          } else {
            return this.shared.post<Cart>(`checkout/carts`, {});
          }
        })
      )
      .pipe(
        tap((res) => {
          if (res && res.data) {
            this.data = res.data;
            if (this.data.isEditing && this.data.editableUntil) {
              this.initIntervalCheckEditFinished();
            } else {
              clearInterval(this.intervalCheckEditFinished);
            }
            this.updateAllPaymentDescription();
          }
        })
      );
  }

  public clearCart() {
    return this.shared.delete<Cart>(`checkout/carts`);
  }

  public clearAllItems(): Observable<Response<Cart>> {
    // Medida paliativa: não limpar carrinho enquanto add/update/remove de promo/item estiver em flight.
    if (this.loadingService.isCartLoading()) {
      return of({
        httpStatus: 0,
        status: false,
        message: "Aguarde a operação do carrinho finalizar.",
        data: null as unknown as Cart,
      });
    }

    const itemsToRemove = [...(this._data?.cartItems || [])];
    const cartToken = this._data?.cartToken;
    this.loadingService.setCartLoading(true);
    return this.shared.delete<Cart>(`checkout/carts/items`).pipe(
      tap((res) => {
        if (res.status && res.data) {
          this.data = res.data;
        }
        if (res.status && itemsToRemove.length > 0) {
          this.trackClearAllItemsRemoved(itemsToRemove, cartToken);
        }
      }),
      finalize(() => this.loadingService.setCartLoading(false)),
    );
  }

  /** GA4 remove_from_cart (bulk) + Salesforce sf_remover_carrinho por item. */
  private trackClearAllItemsRemoved(items: CartItem[], cartToken?: string): void {
    this.analyticsService.trackRemoveFromCartBulk(items);

    const ctx = this.sfCartProductContext();
    for (const cartItem of items) {
      const product = this.cartItemToSfProduct(cartItem);
      this.sfEvents.trackRemoverCarrinho(
        buildSfRemoverCarrinhoParams(product, cartToken, cartItem.quantity ?? 1, ctx),
      );
      this.unmarkSuggestedProduct(product);
    }
  }

  private cartItemToSfProduct(cartItem: CartItem): Product {
    const p = cartItem.product;
    return this.withCatalogFacets({
      id: p.reference || p.code,
      sku: p.code,
      name: p.name,
      price: cartItem.price ?? p.price,
      scalablePrices: p.scalablePrices,
      unitPrice: p.unitPrice,
    } as Product);
  }

  get data() {
    return this._data;
  }

  set data(data: Cart) {
    const previousToken = this._data?.cartToken;
    const sameCart = !!data?.cartToken && data.cartToken === previousToken;
    // Preserva o bloqueio apenas se o servidor omitir a flag.
    // Um false explícito (por exemplo, após excluir tudo) deve reabilitar o botão.
    const keepRecommendedLock = sameCart && data.recommendedProductsAdded === undefined && !!this._data?.recommendedProductsAdded;

    this._data = data ? { ...data } : {};
    if (keepRecommendedLock) {
      this._data.recommendedProductsAdded = true;
    }

    if (data?.cartToken !== previousToken || this._data.recommendedProductsAdded) {
      this.recommendedSelections.clear();
    }
    this.handleCartItems(this._data);
    this.handleCartPromotions(this._data);
    if (this.operation !== "delivery") {
      this.updateAllProducts();
    }
  }

  /** Pedido Rápido já foi usado neste carrinho (botão "Adicionar produtos" desabilitado). */
  get recommendedProductsAdded(): boolean {
    return !!this._data?.recommendedProductsAdded;
  }

  /**
   * Registra quantidade escolhida no carrossel (sem gravar no carrinho).
   * quantity menor que 1 remove a alteração e volta ao padrão do backend (1 unidade).
   */
  setRecommendedSelection(sku: string, quantity: number): void {
    if (!sku) {
      return;
    }
    if (!quantity || quantity < 1) {
      this.recommendedSelections.delete(sku);
      return;
    }
    this.recommendedSelections.set(sku, quantity);
  }

  /** Itens alterados no carrossel para o body do POST /recommended. */
  getRecommendedSelectionItems(): { sku: string; quantity: number }[] {
    const items: { sku: string; quantity: number }[] = [];
    this.recommendedSelections.forEach((quantity, sku) => {
      if (quantity >= 1) {
        items.push({ sku, quantity });
      }
    });
    return items;
  }

  handleCartItems(data) {
    this.cartItemsMap.clear();
    (data.cartItems || []).forEach((item: CartItem) => {
      if (!item.promotionId && item.product?.code) {
        this.cartItemsMap.set(item.product.code, item);
      }
    });

    if (data.cartItems && data.cartItems.length > 0) {
      data.cartProducts = this.itemsToProducts(data.cartItems);
    } else {
      data.cartProducts = [];
    }
  }

  get currentCategory(): ProductAttribute {
    return this._currentCategory;
  }

  set currentCategory(category: ProductAttribute) {
    this._currentCategory = category;
    this.storage.set("currentCategory", category);
    if (category.id === "promotions") {
      this.brands.set([]);
      // Flavor logic is now handled by the effect in the constructor
      return;
    } else {
      this.getBrands(category.id).subscribe((brands: ProductAttribute[]) => {
        brands = brands.map((brand) => {
          if (brand.images && brand.images[0] && brand.images[0].url) {
            brand.image = brand.images[0].url;
          } else {
            brand.image = "";
          }
          return brand;
        });
        this.brands.set(brands);
      });
    }
  }

  get currentBrand(): ProductAttribute {
    return this._currentBrand;
  }

  // handleIsPP(): boolean {
  //   const currentUrl = window.location.href;
  //   if (currentUrl.includes("/tabs/pp")) {
  //     return true;
  //   } else {
  //     return false;
  //   }
  // }

  set currentBrand(brand: ProductAttribute) {
    if (brand && brand.id === "promotions") {
      throw Error("Marca nunca pode ser tratada como promoção");
    }
    this._currentBrand = brand;
    this.storage.set("currentBrand", brand);
    const categoryId = this.currentCategory ? this.currentCategory.id : "";
    const brandId = this.currentBrand ? this.currentBrand.id : "";

    if (categoryId !== "promotions" && categoryId && brandId) {
      this.products.set(undefined);
      this.getProducts(String(categoryId), String(brandId)).subscribe((products: Product[]) => {
        this.products.set(this.getProductsWithCartPaymentDescription(products));
      });
      this.flavors.set(undefined);
      this.getFlavors(categoryId, brandId).subscribe((flavors) => {
        this.flavors.set(flavors);
      });
    } else {
      this.flavors.set(this.promotionsFlavors());
    }
  }

  get currentFlavor(): ProductAttribute | null {
    return this._currentFlavor;
  }

  set currentFlavor(flavor: ProductAttribute | null) {
    this._currentFlavor = flavor;
    this.products.set(undefined);
    const categoryId = this.currentCategory ? this.currentCategory.id : "";
    const brandId = this.currentBrand ? this.currentBrand.id : "";
    const flavorId = this.currentFlavor ? this.currentFlavor.id : "";
    if (categoryId === "promotions") {
      this.promotions.set(undefined);
      this.grpcPromotion
        .getPromotions(
          flavorId
            ? {
                flavorIds: [flavorId],
              }
            : {}
        )
        .then((response) => {
          const res = response.toObject();
          this.promotionImage = res.banner?.url;
          if (res && res.promotions) {
            const productsPromotions = this.promotionsToProducts(res.promotions as Promotion[]);

            this.promotions.set(productsPromotions);
            this.handleCartPromotions();
          } else {
            this.promotions.set([]);
          }
        })
        .catch((error) => {
          console.error("Error gettings promotions: ", error);
        });
    } else {
      this.getProducts(categoryId, brandId, flavorId).subscribe((products: Product[]) => {
        this.products.set(products);
        this.handleCatalog$.next();
      });
    }
  }

  getBrands(categoryId?: string): Observable<ProductAttribute[]> {
    return this.shared.get<ProductResponse<ProductAttribute[]>>(`product/category?ids=${categoryId ? categoryId : ""}`).pipe(
      map((res) => {
        const items = res?.data?.items;
        return items ? items : [];
      })
    );
  }

  getFlavors(categoryId: string, brandId: string): Observable<ProductAttribute[]> {
    return this.shared.get<ProductResponse<ProductAttribute[]>>(`product/category?ids=${categoryId},${brandId}`).pipe(
      map((res) => {
        const items = res?.data?.items;
        return items ? items : [];
      })
    );
  }

  getProductsByIds(ids: string[]): Observable<Product[]> {
    const queryParams: string[] = ids;
    const fullEndpoint = `product?ids=${queryParams.join(",")}`;
    return this.shared.get<ProductResponse<Product[]>>(fullEndpoint).pipe(
      map((res) => {
        if (res && res.data && res.data.items) {
          const prods = this.productsDTO(res.data.items);
          return prods;
        } else {
          return [];
        }
      })
    );
  }

  getProducts(categoryId?: string, brandId?: string, flavorId?, q?: string): Observable<Product[]> {
    if (!q && categoryId && !brandId) {
      console.error("Buscar produtos sem marca não é possível");
      return of([]);
    }
    const queryParams: string[] = [];

    if (categoryId && !q) {
      queryParams.push(categoryId);
    } else {
      this.currentCategory && !q ? queryParams.push(this.currentCategory.id) : null;
    }
    if (brandId && !q) {
      queryParams.push(brandId);
    } else {
      this.currentBrand && !q ? queryParams.push(this.currentBrand.id) : null;
    }
    if (flavorId && !q) {
      queryParams.push(flavorId);
    }
    if (!q) {
      q = "";
    }

    // const isPagePP = this.handleIsPP();
    // const isCartPP = this.data.isRedeemable;
    //
    //
    // let targetPaymentCondition = this.paymentCondition;
    // if (isCartPP && !isPagePP) {
    //   targetPaymentCondition = "";
    // }

    const size = q ? 5 : "";
    this.cancelPreviousProductsRequest$.next();
    const fullEndpoint = `product?q=${q}&categories=${queryParams.join(",")}&paymentCondition=${this.paymentCondition}&size=${size}`;
    return this.shared.get<ProductResponse<Product[]>>(fullEndpoint).pipe(
      takeUntil(this.cancelPreviousProductsRequest$),
      map((res) => {
        if (res && res.data && res.data.items) {
          const prods = this.productsDTO(res.data.items);
          return prods;
        } else {
          return [];
        }
      })
    );
  }

  private getResetedScalablePrices(product: Product): ScalablePrice[] | undefined {
    if (product && product.scalablePrices) {
      return product.scalablePrices.map((scalable, i) => {
        if (i === 0) {
          return {
            ...scalable,
            active: true,
          };
        } else {
          return {
            ...scalable,
            active: false,
          };
        }
      });
    } else {
      return undefined;
    }
  }

  private updateProduct(product: Product) {
    if (product.isCombo || product.isKit || product.promotionId) {
      return;
    }
    let scalablePrices = product.scalablePrices;
    const cartItem = this.cartItemsMap.get(product.sku);
    if (cartItem) {
      scalablePrices = cartItem.product.scalablePrices;
      product.scalablePrices = scalablePrices;
      product.quantity = cartItem.quantity ?? 0;
      product.inputQuantity = product.quantity;
    } else {
      product.quantity = 0;
      product.inputQuantity = 0;
      product.scalablePrices = this.getResetedScalablePrices(product);
    }
    const indexPrice = scalablePrices?.findIndex((scalable) => scalable.active);
    if (indexPrice !== undefined && indexPrice >= 0) {
      const current = scalablePrices ? scalablePrices[indexPrice] : null;
      const next = scalablePrices ? scalablePrices[indexPrice + 1] : null;
      if (current) {
        product.currentScalable = current;
      } else {
        product.currentScalable = undefined;
      }
      if (next) {
        product.nextScalable = next;
      } else {
        product.nextScalable = undefined;
      }
    }
    if (product.id === this.currentProduct?.id) {
      this.currentProduct$.next(product);
    }
  }

  private updateProductWithoutCart(product: Product) {
    const indexPrice = product.scalablePrices?.findIndex((scalable) => scalable.active);
    if (indexPrice !== undefined && indexPrice >= 0) {
      const current = product.scalablePrices ? product.scalablePrices[indexPrice] : null;
      const next = product.scalablePrices ? product.scalablePrices[indexPrice + 1] : null;
      if (current) {
        product.currentScalable = current;
      } else {
        product.currentScalable = undefined;
      }
      if (next) {
        product.nextScalable = next;
      } else {
        product.nextScalable = undefined;
      }
    }
  }

  promotionsToProducts(promotions: Promotion[]): Product[] {
    const products: Product[] = promotions.map((promotion) => {
      const product: Product = {
        ...promotion,
        id: promotion.sku,
        label: promotion.promotionDescription,
        sku: promotion.sku,
        type: promotion.type?.toString(), // Convert number to string
        inputQuantity: promotion.qnt ? promotion.qnt : 0,
        quantity: promotion.qnt ? promotion.qnt : 0,
        promotional: true,
        price: promotion.price,
        paymentCondition: promotion.items[0].paymentCondition,
        paymentConditionDescription: promotion.items[0].paymentConditionDescription,
        isKit: promotion.type === PromotionModel.promotion.v1.PromotionType.KIT,
        isCombo: promotion.type === PromotionModel.promotion.v1.PromotionType.COMBO,
        rejectionMessage: "",
        points: promotion.points ? promotion.points : 0,
        items: promotion.items
          ? promotion.items.map((promotionItem) => {
              return {
                ...promotionItem,
                promotionId: promotion.sku,
                id: promotionItem.productId ? promotionItem.productId : promotionItem.sku,
                label: promotionItem.label ? promotionItem.label : "Label do produto não cadastrado",
                image: promotionItem.images && promotionItem.images.length > 0 ? promotionItem.images[0].url : "",
                sku: promotionItem.sku,
                quantityPromotionDetails: promotionItem.quantityPromotionDetails || promotionItem.quantity || 0,
                promotional: true,
                isKit: promotion.type === PromotionModel.promotion.v1.PromotionType.KIT,
                isCombo: promotion.type === PromotionModel.promotion.v1.PromotionType.COMBO,
                rejectionMessage: "",
                redeemPoints: promotionItem.redeemPoints ? promotionItem.redeemPoints : 0,
                redeemOriginalPoints: promotionItem.redeemOriginalPoints ? promotionItem.redeemOriginalPoints : 0,
                paymentConditionDescription: promotionItem.paymentConditionDescription ? promotionItem.paymentConditionDescription : "",
                attributes: {
                  scalable: promotionItem && promotionItem.scalablePrices && promotionItem.scalablePrices.length > 1 ? true : false,
                  capacityLabel: promotionItem.attributes?.capacityLabel,
                  weight: promotionItem.attributes?.weight ? promotionItem.attributes?.weight / 100 : 0,
                  quantityPerPackage: promotionItem.attributes?.quantityPerPackage,
                },
              };
            })
          : [],
      };
      if (!product.image) {
        product.image = product && product.images && product.images?.length > 0 ? product.images[0].url : "";
      } else if (product.image && product.image.url) {
        product.image = product.image.url;
      }
      return product;
    });
    (products || []).forEach(this.updateProduct.bind(this));
    return products;
  }

  itemsToProducts(items: CartItem[], noCart?: boolean): Product[] {
    const products: Product[] = items.map((item) => {
      const product: Product = {
        ...item.product,
        promotionId: item.promotionId ? item.promotionId : "",
        promotional_free: item.promotional ? item.promotional : false,
        isKitCombo: item.promotionId ? true : false,
        id: item.product.reference,
        label: item.product.name ? item.product.name : "",
        sku: item.product.code,
        inputQuantity: item.quantity ? item.quantity : 0,
        quantity: item.quantity ? item.quantity : 0,
        promotional: item.promotional ? item.promotional : false,
        rejectionMessage: item.rejectionMessage ? item.rejectionMessage : "",
        redeemPoints: item.product.redeemPoints ? item.product.redeemPoints : 0,
        redeemOriginalPoints: item.product.redeemOriginalPoints ? item.product.redeemOriginalPoints : 0,
        paymentConditionDescription: item.product.paymentConditionDescription ? item.product.paymentConditionDescription : "",
        attributes: {
          scalable: item.product && item.product.scalablePrices && item.product.scalablePrices.length > 1 ? true : false,
          capacityLabel: item.product.capacityLabel,
          weight: item.product.weight ? item.product.weight / 100 : 0,
          quantityPerPackage: item.product.quantityPerPackage,
        },
      };
      return product;
    });
    if (noCart) {
      (products || []).forEach(this.updateProductWithoutCart.bind(this));
    } else {
      (products || []).forEach(this.updateProduct.bind(this));
    }
    return products;
  }

  getSummaryItems(c: Checkout, full?: boolean): SummaryItem[] {
    const summary: SummaryItem[] = [];
    if (c) {
      if (c.delivery) {
        summary.push({
          label: "Tipo de entrega",
          value: c.delivery.label,
        });
      }
      if (c.shipping) {
        summary.push({
          label: "Taxa de entrega",
          price: c.shipping,
        });
      }
      if (c.payment) {
        summary.push({
          label: "Forma de pagamento",
          value: c.payment.paymentMethod === "pix" ? "PIX" : c.payment.paymentMethodDescription,
        });
        summary.push({
          label: "Cond. de pagamento",
          value: c.payment.description,
        });
      }
      if (c.subtotal) {
        summary.push({
          label: "Subtotal",
          price: c.subtotal,
        });
      }
      if (c.remainingBalance) {
        summary.push({
          label: "Saldo disponível",
          price: c.remainingBalance,
        });
      }
      if (c.discount) {
        summary.push({
          label: "Descontos",
          price: c.discount,
        });
      }
      if (full) {
        summary.push({
          label: "Total",
          price: c.amount,
        });
      }
    }
    return summary;
  }

  getSummaryPointsItems(c: Checkout): SummaryItem[] {
    const summary: SummaryItem[] = [];
    if (c) {
      summary.push({
        label: "Subtotal",
        value: c.subtotal_redeem_points ? String(c.subtotal_redeem_points) : "0",
      });
      summary.push({
        label: "Subtotal frete",
        value: c.shipping ? String(c.shipping) : "0",
      });
      summary.push({
        label: "Total",
        value: c.totalRedeemPoints ? String(c.totalRedeemPoints) : "0",
      });
    }
    return summary;
  }

  /**
   * "Adicionar produtos" do Pedido Rápido: uma única chamada que joga toda a lista de
   * produtos recomendados do cliente no carrinho (o backend valida item a item e
   * recalcula o carrinho uma única vez no final).
   *
   * `isRedeemable` vai como 1/0 — a validação do backend recusa true/false. Quando o
   * tipo diverge do carrinho atual o backend converte o carrinho, removendo os itens
   * do tipo anterior; por isso o diff abaixo também emite os eventos de remoção.
   *
   * Body opcional `items`: só SKUs alterados no carrossel (quantity >= 1). Demais
   * recomendados entram com 1 unidade. Sem body = todos com 1 unidade.
   */
  addAllRecommendedItems(isRedeemable?: boolean): Observable<Response<Cart> & { addedProducts: number }> {
    if (this.loadingService.isCartLoading()) {
      return of({
        httpStatus: 0,
        status: false,
        message: "Aguarde a operação do carrinho finalizar.",
        data: null as unknown as Cart,
        addedProducts: 0,
      });
    }

    if (this.recommendedProductsAdded) {
      return of({
        httpStatus: 400,
        status: false,
        message: "Os produtos recomendados já foram adicionados a este carrinho.",
        data: null as unknown as Cart,
        addedProducts: 0,
      });
    }

    const clientId = this.shared.user?.clientId;
    if (!clientId) {
      return of({
        httpStatus: 0,
        status: false,
        message: "Não foi possível identificar o cliente. Faça login novamente.",
        data: null as unknown as Cart,
        addedProducts: 0,
      });
    }

    const params = new URLSearchParams({ clientId: String(clientId) });
    if (typeof isRedeemable === "boolean") {
      params.set("isRedeemable", isRedeemable ? "1" : "0");
    }

    const previousItems = [...(this._data?.cartItems || [])];
    const previousCartToken = this._data?.cartToken;
    // Conversão dinheiro <-> resgate: o backend limpa o carrinho antes de popular.
    const cartConverted = typeof isRedeemable === "boolean" && previousItems.length > 0 && !!this._data?.isRedeemable !== isRedeemable;
    const selectionItems = this.getRecommendedSelectionItems();
    const body = selectionItems.length > 0 ? { items: selectionItems } : {};
    this.loadingService.setCartLoading(true);

    return this.shared.post<Cart>(`checkout/carts/cart-items/recommended?${params.toString()}`, body).pipe(
      map((res) => {
        if (!res.status || !res.data) {
          return { ...res, addedProducts: 0 };
        }
        // Garante bloqueio imediato do botão mesmo se o backend omitir o campo na resposta.
        this.data = { ...res.data, recommendedProductsAdded: true };
        const addedProducts = this.trackRecommendedBulkChanges(previousItems, this.data, !!isRedeemable, cartConverted, previousCartToken);
        return { ...res, data: this.data, addedProducts };
      }),
      finalize(() => this.loadingService.setCartLoading(false))
    );
  }

  /**
   * GA4 + Salesforce do "Adicionar todos": compara o carrinho anterior com o devolvido
   * pela API e reporta apenas o delta real. Retorna quantos produtos entraram.
   */
  private trackRecommendedBulkChanges(previousItems: CartItem[], cart: Cart, isRedeemable: boolean, cartConverted: boolean, previousCartToken?: string): number {
    // Em carrinho convertido nada do estado anterior sobrevive: tudo saiu, tudo que voltou entrou.
    const remainingPrevious = new Map<string, number>();
    if (!cartConverted) {
      previousItems.forEach((item) => {
        const code = item.product?.code;
        if (code) {
          remainingPrevious.set(code, (remainingPrevious.get(code) ?? 0) + (item.quantity ?? 0));
        }
      });
    }

    const addedProducts: Product[] = [];
    (cart.cartItems || []).forEach((item) => {
      const code = item.product?.code;
      if (!code) {
        return;
      }
      const addedQuantity = (item.quantity ?? 0) - (remainingPrevious.get(code) ?? 0);
      remainingPrevious.delete(code);
      if (addedQuantity <= 0) {
        return;
      }
      const product = this.findRecommendedProduct(code, isRedeemable) ?? this.cartItemToSfProduct(item);
      addedProducts.push({ ...product, quantity: addedQuantity, inputQuantity: addedQuantity });
    });

    // O que sobrou no mapa existia antes e sumiu do carrinho devolvido pela API.
    const removedItems = cartConverted ? previousItems : previousItems.filter((item) => !!item.product?.code && remainingPrevious.has(item.product.code));
    if (removedItems.length > 0) {
      this.trackClearAllItemsRemoved(removedItems, previousCartToken);
    }

    if (addedProducts.length > 0) {
      this.analyticsService.trackAddSuggestedProductsToCart(addedProducts, isRedeemable);
      const ctx = this.sfCartProductContext();
      addedProducts.forEach((product) => {
        this.markSuggestedProduct(product);
        this.analyticsMapper.rememberProductFacets(product);
        this.sfEvents.trackAdicionarCarrinho(
          buildSfAdicionarCarrinhoParams(this.withCatalogFacets(product), cart.cartToken, product.quantity ?? 1, ctx),
        );
      });
    }

    return addedProducts.length;
  }

  /** Reencontra o Product completo do catálogo sugerido, para enriquecer os eventos. */
  private findRecommendedProduct(code: string, isRedeemable: boolean): Product | undefined {
    const list = (isRedeemable ? this.recommendedPointsProducts() : this.recommendedProducts()) || [];
    return list.find((product) => product.sku === code || product.id === code);
  }

  addItem(product: Product, notNotify?: boolean, isRedeemable?: boolean, forceRedirectCart?: boolean, previousQuantity?: number, source?: "recommended") {
    this.loadingService.setCartLoading(true);

    const isComboOrKit = product.isCombo || product.isKit;
    const httpMethod = isComboOrKit ? "post" : "put";
    const payload = isComboOrKit
      ? {
          quantity: product.inputQuantity ? Number(product.inputQuantity) : 1,
          isRedeemable: isRedeemable ? true : false,
        }
      : {
          item: {
            quantity: product.inputQuantity ? Number(product.inputQuantity) : 1,
            isRedeemable: isRedeemable ? true : false,
            promotion_sku: product.promotionId ? product.promotionId : "",
          },
        };
    this.shared[httpMethod]<Cart>(`checkout/carts/${isComboOrKit ? "cart-promotions" : "cart-items"}/${product.id}`, payload).subscribe((res) => {
      this.loadingService.setCartLoading(false);
      if (res.status) {
        this.data = res.data;

        // GA4: add_to_cart ao adicionar produto com sucesso
        const qty = product.inputQuantity ? Number(product.inputQuantity) : 1;
        if (source === "recommended") {
          this.markSuggestedProduct(product);
          this.analyticsService.trackAddSuggestedProductToCart(product, qty, !!isRedeemable);
        } else {
          this.analyticsService.trackAddToCart(product, qty);
        }

        // Salesforce: sf_adicionar_carrinho (memoriza marca/categoria para as operações dentro do carrinho)
        this.analyticsMapper.rememberProductFacets(product);
        this.sfEvents.trackAdicionarCarrinho(
          buildSfAdicionarCarrinhoParams(this.withCatalogFacets(product), this.data?.cartToken, qty, this.sfCartProductContext()),
        );

        const isEditing = this.data.isEditing;
        if (!notNotify) {
          this.changeProduct$.next({ id: product.id, action: "add" });
        }
        if (forceRedirectCart) {
          this.router.navigateByUrl(isRedeemable ? "tabs/pp/cart-redeem" : isEditing ? "tabs/cart/editing" : "tabs/cart");
        }
      } else {
        if (typeof previousQuantity === "number" && !isNaN(previousQuantity)) {
          product.inputQuantity = previousQuantity;
          product.quantity = previousQuantity;
        } else {
          product.inputQuantity = 0; // Se não houver quantidade anterior, reverte para 0
          product.quantity = 0;
        }

        if (product.isCombo || product.isKit) {
          this.system.showAlert("warn", "Atenção", res.message ? res.message : "Erro ao adicionar combo ou kit", "Ok, entendi", () => {});
        } else {
          this.system.showErrorAlert(new Error(res.message ? res.message : "Erro ao adicionar produto"));
        }
        this.updateCart$.next(this.data); // Notifica a UI para reverter a quantidade
      }
    });
  }

  updateItem(product, notNotify?: boolean, isRedeemable?: boolean, forceRedirectCart?: boolean, previousQuantity?: number, source?: "recommended") {
    const isComboOrKit = product.isCombo || product.isKit;
    const httpMethod = isComboOrKit ? "post" : "put";
    const payload = isComboOrKit
      ? {
          quantity: product.inputQuantity ? Number(product.inputQuantity) : 1,
          isRedeemable: isRedeemable ? true : false,
        }
      : {
          item: {
            quantity: product.inputQuantity ? Number(product.inputQuantity) : 1,
            isRedeemable: isRedeemable ? true : false,
            promotion_sku: product.promotionId ? product.promotionId : "",
          },
        };

    this.loadingService.setCartLoading(true);
    this.shared[httpMethod]<Cart>(`checkout/carts/${isComboOrKit ? "cart-promotions" : "cart-items"}/${product.id}`, payload).subscribe((res) => {
      this.loadingService.setCartLoading(false);

      if (res.status) {
        this.data = res.data;

        // GA4: rastrear alteração de quantidade no carrinho
        const newQty = product.inputQuantity ? Number(product.inputQuantity) : 1;
        const prevQty = typeof previousQuantity === "number" ? previousQuantity : 0;
        const isSuggested = source === "recommended" || this.isSuggestedProduct(product);
        if (source === "recommended") {
          this.markSuggestedProduct(product);
        }
        if (newQty > prevQty) {
          if (isSuggested) {
            this.analyticsService.trackAddSuggestedProductToCart(product, newQty - prevQty, !!isRedeemable);
          } else {
            this.analyticsService.trackAddToCart(product, newQty - prevQty);
          }

          // Salesforce: cada incremento confirmado também é uma adição ao carrinho.
          this.analyticsMapper.rememberProductFacets(product);
          this.sfEvents.trackAdicionarCarrinho(
            buildSfAdicionarCarrinhoParams(
              this.withCatalogFacets(product),
              this.data?.cartToken,
              newQty - prevQty,
              this.sfCartProductContext(),
            ),
          );
        } else if (newQty < prevQty) {
          if (isSuggested) {
            this.analyticsService.trackRemoveSuggestedProductFromCart(product, prevQty - newQty, !!isRedeemable);
          } else {
            this.analyticsService.trackRemoveFromCart(product, prevQty - newQty);
          }

          // Salesforce: sf_remover_carrinho (diminuir quantidade)
          this.sfEvents.trackRemoverCarrinho(
            buildSfRemoverCarrinhoParams(
              this.withCatalogFacets(product),
              this.data?.cartToken,
              prevQty - newQty,
              this.sfCartProductContext(),
            ),
          );
        }

        const isEditing = this.data.isEditing;
        if (!notNotify) {
          this.changeProduct$.next({ id: product.id, action: "update" });
        }
        if (forceRedirectCart) {
          this.router.navigateByUrl(isRedeemable ? "tabs/pp/cart-redeem" : isEditing ? "tabs/cart/editing" : "tabs/cart");
        }
      } else {
        if (typeof previousQuantity === "number" && !isNaN(previousQuantity)) {
          product.inputQuantity = previousQuantity;
          product.quantity = previousQuantity;

          // Encontra o item no carrinho e reverte a quantidade nele também
          const itemInCart = this.data.cartItems?.find((item) => item.product.reference === product.id);
          if (itemInCart) {
            itemInCart.quantity = previousQuantity;
          }
        } else if (typeof product.quantity === "number" && !isNaN(product.quantity)) {
          product.inputQuantity = product.quantity;
        }

        if (product.isCombo || product.isKit) {
          this.system.showAlert("warn", "Atenção", res.message ? res.message : "Erro ao atualizar combo ou kit", "Ok, entendi", () => {});
        } else {
          this.system.showErrorAlert(new Error(res.message ? res.message : "Erro ao atualizar produto"));
        }
        this.updateCart$.next(this.data); // Notifica a UI para reverter a quantidade
      }
    });
  }

  removeItem(product, previousQuantity?: number, source?: "recommended") {
    // Medida paliativa: evita remoção concorrente durante add/update de promo (bug preço 0,01).
    if (this.loadingService.isCartLoading()) {
      return;
    }
    this.loadingService.setCartLoading(true);
    const isSuggested = source === "recommended" || this.isSuggestedProduct(product);
    this.shared
      .delete<Cart>(`checkout/carts/cart-items/${product.id}`, {
        promotion_sku: product.promotionId ? product.promotionId : "",
      })
      .subscribe((res) => {
        this.loadingService.setCartLoading(false);
        if (res.status) {
          // GA4: remove_from_cart ao remover produto com sucesso
          const removedQty = typeof previousQuantity === "number" ? previousQuantity : product.quantity || 1;
          if (isSuggested) {
            this.analyticsService.trackRemoveSuggestedProductFromCart(product, removedQty);
          } else {
            this.analyticsService.trackRemoveFromCart(product, removedQty);
          }

          // Salesforce: sf_remover_carrinho (lixeira)
          this.sfEvents.trackRemoverCarrinho(
            buildSfRemoverCarrinhoParams(
              this.withCatalogFacets(product),
              this.data?.cartToken,
              removedQty,
              this.sfCartProductContext(),
            ),
          );

          product.inputQuantity = 0;
          product.quantity = 0;
          product.quantityCombo = 0;
          this.unmarkSuggestedProduct(product);
          this.data = res.data;
          this.changeProduct$.next({ id: product.id, action: "update" });
        } else {
          if (typeof previousQuantity === "number" && !isNaN(previousQuantity)) {
            product.inputQuantity = previousQuantity;
            product.quantity = previousQuantity;
          }
          if (product.isCombo || product.isKit) {
            this.system.showAlert("warn", "Atenção", res.message ? res.message : "Erro ao remover combo ou kit", "Ok, entendi", () => {});
          } else {
            this.system.showErrorAlert(new Error(res.message ? res.message : "Erro ao remover produto"));
          }
          this.updateCart$.next(this.data);
        }
      });
  }

  updateAllPaymentDescription() {
    const currentProducts = this.products();
    const recommendedProducts = this.recommendedProducts();
    const currentProductsUpdated = this.getProductsWithCartPaymentDescription(currentProducts);
    const recommendedProductsUpdated = this.getProductsWithCartPaymentDescription(recommendedProducts);
    this.handleCartPromotions();
    this.products.set(currentProductsUpdated);
    this.recommendedProducts.set(recommendedProductsUpdated);
  }

  getProductsWithCartPaymentDescription(products: Product[] = []) {
    if (!products || products.length === 0) {
      return [];
    }
    if (this.data && this.data.cartItems && this.data.cartItems.length > 0) {
      const defaultDescription = this.data.cartItems[0].product.paymentConditionDescription;
      const defaultPaymentCondition = this.data.cartItems[0].product.paymentCondition;
      if (defaultPaymentCondition) {
        this.paymentCondition = defaultPaymentCondition;
        this.getProducts().subscribe(() => {});
      } else {
        this.paymentCondition = "";
      }
      if (!defaultDescription) {
        return products;
      }

      // const isPagePP = this.handleIsPP();
      // const isCartPP = this.data.isRedeemable;
      // let targetPaymentDescription = defaultDescription;
      // if (isCartPP && !isPagePP) {
      //   targetPaymentDescription = "PIX";
      // }
      const prods = products.map((p) => {
        p.paymentConditionDescription = defaultDescription;
        return p;
      });
      return prods;
    } else {
      return products;
    }
  }

  private productsDTO(products: Product[]): Product[] {
    return products.map((product) => {
      if (!product.attributes) {
        product.attributes = {};
      }
      product.attributes.scalable = product.scalablePrices && product.scalablePrices.length > 1 ? true : false;
      if (!product.image) {
        product.image = product && product.images && product.images?.length > 0 ? product.images[0].url : "";
      }
      this.updateProduct(product);
      return product;
    });
  }

  private updateAllProducts() {
    (this.products() || []).forEach(this.updateProduct.bind(this));
    this.syncRecommendedProductsDisplay();
  }

  /** Sincroniza display do Pedido Rápido: seleção local ou quantidades do carrinho. */
  private syncRecommendedProductsDisplay() {
    const selectionMode = !this.recommendedProductsAdded;
    (this.recommendedProducts() || []).forEach((product) => {
      if (selectionMode) {
        this.updateRecommendedSelectionProduct(product);
      } else {
        this.updateProduct(product);
      }
    });
    (this.recommendedPointsProducts() || []).forEach((product) => {
      if (selectionMode) {
        this.updateRecommendedSelectionProduct(product);
      } else {
        this.updateProduct(product);
      }
    });
  }

  /**
   * Pedido Rápido antes do "Adicionar produtos": quantidade é só seleção local
   * (padrão 1, alinhado ao backend). Não sincroniza com o carrinho para evitar
   * somar de novo a mesma quantidade no POST /recommended.
   */
  private updateRecommendedSelectionProduct(product: Product) {
    if (product.isCombo || product.isKit || product.promotionId) {
      return;
    }
    const sku = product.sku || product.id;
    const selection = sku ? this.recommendedSelections.get(sku) : undefined;
    const qty = selection ?? 1;
    product.quantity = qty;
    product.inputQuantity = qty;
    if (!product.scalablePrices?.some((s) => s.active)) {
      product.scalablePrices = this.getResetedScalablePrices(product);
    }
    this.updateProductWithoutCart(product);
  }
}
